import { Search, Bot, Lock, ChevronsUpDown, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18n from '@/Config/app/i18n';
import { IndexPath } from '@/Config/app/router/index.path';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { McpClaudeIcon } from '@/Domain/components/icons/McpClaude.icon';
import { OpenAiIcon } from '@/Domain/components/icons/OpenAi.icon';
import type { AgentCreateSource } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useCreateAgent } from '@/Domain/hooks/App/useCreateAgent.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
interface SidebarAIAssistantSwitcherProps {
  aiAssistantsData?: AIAssistantsData;
  hasSubscriptionAccess?: boolean;
}

// getTrierLabel maps both legacy and new-pricing (`*_2026`) tier values to their display name.
const getPlanDisplayName = (pricingTier?: TierEnum) =>
  pricingTier
    ? SubscriptionHelper.getTrierLabel(pricingTier)
    : i18n.t('sidebar_menu:ai_agent_label');

export const SidebarAIAssistantSwitcher = ({
  aiAssistantsData,
  hasSubscriptionAccess,
}: SidebarAIAssistantSwitcherProps) => {
  const { t } = useTranslation('sidebar_menu');
  const navigate = useNavigate();
  const { isMobile, portalContainer } = useSidebar();

  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const { toggleModal } = useModalStore();

  const handleScenarioChange = (scenarioId: string) => window.location.replace(`/i/${scenarioId}`);
  // Guard state lives here, not on the menu item: Radix unmounts the item on select.
  const { isCreating, createAgent } = useCreateAgent();
  const handleCreate = (): void => createAgent('agent_switcher' satisfies AgentCreateSource);
  const handleUpdatePlan = (): void => navigate(IndexPath.subscription, { viewTransition: true });

  const { current_ai_assistant: currentAiAssistant, all_ai_assistants: allAiAssistants } =
    aiAssistantsData || {};

  const isLoading = !aiAssistantsData;

  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredAssistants = useMemo(() => {
    if (!allAiAssistants) return [];
    if (!searchQuery.trim()) return allAiAssistants;
    const query = searchQuery.toLowerCase();
    return allAiAssistants.filter((assistant) => assistant.name?.toLowerCase().includes(query));
  }, [allAiAssistants, searchQuery]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearchQuery('');
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu modal={false} onOpenChange={handleOpenChange}>
          <DropdownMenuTrigger asChild disabled={isLoading}>
            <SidebarMenuButton className="h-[34px] gap-1.5 rounded-xl border border-sidebar-border bg-muted p-1.5 hover:!bg-sidebar-accent data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:!p-[5px]">
              <div className="flex aspect-square size-5 items-center justify-center rounded-md">
                {currentAiAssistant?.avatar_image ? (
                  <Avatar className="size-5 rounded-md">
                    <AvatarImage src={currentAiAssistant.avatar_image} />
                    <AvatarFallback className="rounded-md text-xs">
                      {currentAiAssistant?.name?.charAt(0) || 'AI'}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Bot className="size-4" />
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0 text-left">
                {currentAiAssistant ? (
                  <span className="truncate text-sm font-medium text-sidebar-foreground">
                    {currentAiAssistant?.name}
                  </span>
                ) : (
                  <Skeleton className="h-4 w-24" />
                )}
              </div>
              <ChevronsUpDown className="ml-auto size-4 !text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72 max-h-[60dvh] flex flex-col overflow-hidden"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
            container={isMobile ? portalContainer : undefined}
          >
            {/* Sticky Header - Search */}
            <DropdownMenuLabel>{t('ai_agent_label')}</DropdownMenuLabel>
            <div className="px-1.5 pb-1 shrink-0">
              <Input
                ref={searchInputRef}
                placeholder={t('search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                size="sm"
                startIcon={<Search className="size-4" />}
              />
            </div>
            <DropdownMenuSeparator className="shrink-0" />

            {/* Scrollable List */}
            <div className="flex-1 overflow-y-auto custom-scroll min-h-0">
              {isLoading ? (
                <div className="p-2 space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : filteredAssistants.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  {t('no_results', 'No AI Agents found')}
                </div>
              ) : (
                filteredAssistants.map((assistant) => (
                  <DropdownMenuItem
                    key={assistant.scenario_id}
                    onClick={() => handleScenarioChange(assistant.scenario_id)}
                    className="flex gap-2 items-center py-1 px-2 w-full"
                  >
                    <div className="size-8 relative rounded-lg flex items-center justify-center">
                      {assistant?.avatar_image ? (
                        <Avatar className="size-8 rounded-lg">
                          <AvatarImage
                            src={assistant.avatar_image}
                            className="w-full h-full object-cover"
                          />
                          <AvatarFallback className="rounded-lg text-xs">
                            {assistant.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Bot className="size-4 shrink-0" />
                      )}
                    </div>
                    <div className="flex flex-col ml-0.5 min-w-0 flex-1">
                      <span className="text-sm font-medium leading-none mt-1 truncate pb-px">
                        {assistant.name}
                      </span>
                      <span className="text-xs font-normal text-muted-foreground -mt-px truncate">
                        {getPlanDisplayName(assistant.pricing_tier)}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
            </div>

            {/* Sticky Footer - Add/Upgrade */}
            {!isSubscriptionFetched || isActivated('can_add_bot') ? (
              <div className="shrink-0">
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleCreate}
                  disabled={isCreating}
                  aria-busy={isCreating}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border border-border bg-background">
                    {isCreating ? <Spinner className="size-4" /> : <Plus className="size-4" />}
                  </div>
                  <div className="font-medium">{t('button.add_ai_agent')}</div>
                </DropdownMenuItem>
              </div>
            ) : (
              hasSubscriptionAccess && (
                <div className="shrink-0">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleUpdatePlan} className="gap-2 p-2">
                    <div className="flex size-6 items-center justify-center rounded-md border border-border bg-background">
                      <Lock className="size-4" />
                    </div>
                    <div className="font-medium text-muted-foreground">
                      {t('button.upgrade_plan')}
                    </div>
                  </DropdownMenuItem>
                </div>
              )
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex justify-center group-data-[collapsible=icon]:hidden">
          <Button
            variant="outline"
            type="button"
            onClick={() => toggleModal(MODAL_NAMES.ACCOUNT_MCP_SHEET, { source: 'sidebar_pill' })}
            className="flex cursor-pointer items-center gap-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-900 border-indigo-100 dark:bg-indigo-900 dark:hover:bg-indigo-800 dark:border-indigo-800 dark:text-indigo-200 rounded-b-lg rounded-t-none h-auto border border-t-0 px-3! py-0.5! text-xs"
            data-testid="sidebar-account-mcp-attached"
          >
            <span>{t('account_mcp:manage_cta_short')}</span>
            <OpenAiIcon className="size-3! shrink-0" />
            <McpClaudeIcon className="size-3! shrink-0" />
          </Button>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
